import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, object = authed
  const [error, setError] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    };
    init();
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("ds_token", data.access_token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data.access_token) localStorage.setItem("ds_token", data.access_token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ds_token");
    localStorage.removeItem("ds_admin_token");
    setUser(false);
  };

  // Admin-only: switch the current session to another user without logout.
  // The original admin token is preserved in localStorage so we can return back.
  const impersonate = async (userId) => {
    try {
      const { data } = await api.post(`/users/${userId}/impersonate`);
      const currentToken = localStorage.getItem("ds_token");
      if (currentToken && !localStorage.getItem("ds_admin_token")) {
        localStorage.setItem("ds_admin_token", currentToken);
      }
      localStorage.setItem("ds_token", data.access_token);
      // Refresh /auth/me to pull permissions + impersonation context
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  // Return to the original admin session
  const stopImpersonate = async () => {
    const adminToken = localStorage.getItem("ds_admin_token");
    if (!adminToken) return false;
    localStorage.setItem("ds_token", adminToken);
    localStorage.removeItem("ds_admin_token");
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return true;
    } catch {
      setUser(false);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, error, impersonate, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
